import React, { useState, useRef, useEffect } from 'react';
import './SearchBar.scss';

const SearchBar = ({ searchTerm, onSearchChange, placeholder = "Search...", searchResults = [], onResultClick }) => {
  const [showResults, setShowResults] = useState(false);
  const searchContainerRef = useRef(null);

  const handleInputChange = (e) => {
    onSearchChange(e.target.value);
  };

  const handleClear = () => {
    onSearchChange('');
    setShowResults(false);
  };

  const handleResultClick = (result) => {
    onResultClick(result);
    setShowResults(false);
  };

  const getMethodColor = (method) => {
    const colors = {
      GET: 'success',
      POST: 'warning', 
      PUT: 'info',
      PATCH: 'accent',
      DELETE: 'error'
    };
    return colors[method.toUpperCase()] || 'accent';
  };

  // Show results when there's a search term and results
  useEffect(() => {
    setShowResults(searchTerm.trim() !== '' && searchResults.length > 0);
  }, [searchTerm, searchResults]);

  // Close results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="search-bar" ref={searchContainerRef}>
      <div className="search-input-container">
        <div className="search-icon">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"></circle>
            <path d="m21 21-4.35-4.35"></path>
          </svg>
        </div>
        <input
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          placeholder={placeholder}
          className="search-input"
          onFocus={() => setShowResults(searchTerm.trim() !== '' && searchResults.length > 0)}
        />
        {searchTerm && (
          <button 
            onClick={handleClear}
            className="clear-button"
            title="Clear search"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        )}
      </div>
      
      {/* Floating Search Results */}
      {showResults && (
        <div className="search-results-floating">
          <div className="search-results-header">
            <span className="results-count">{searchResults.length} result{searchResults.length !== 1 ? 's' : ''}</span>
            <button 
              onClick={() => setShowResults(false)}
              className="close-results"
              title="Close results"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
          <div className="search-results-list">
            {searchResults.map((result) => (
              <button
                key={result.id}
                className="search-result-item"
                onClick={() => handleResultClick(result)}
              >
                <div className="result-left">
                  <span className={`method-badge ${getMethodColor(result.method)}`}>
                    {result.method}
                  </span>
                  <div className="result-info">
                    <span className="result-title">{result.title}</span>
                    <span className="result-path">{result.path}</span>
                  </div>
                </div>
                <div className="result-right">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="m9 18 6-6-6-6"/>
                  </svg>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchBar;
